import React, { useState } from "react";
import { Box, Button, ButtonProps, styled, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

const QuickRange = styled(
  ({ active, ...props }: ButtonProps & { active?: boolean }) => (
    <Button variant="text" size="small" {...props} />
  ),
  {
    shouldForwardProp: (prop) => prop !== "active",
  }
)(({ theme, active }) => ({
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  ...theme.typography.caption,
  height: 20,
  minWidth: "auto",
  padding: theme.spacing(3, 2),
  fontWeight: active ? 500 : 400,
  backgroundColor: active
    ? theme.palette.background.neutralDark
    : "transparent",
}));

type Props = {
  onRelativePreset: (monthsBack: number, key: string) => void;
  onYearPreset: (year: number) => void;
  activeKey?: string | null;
};

export const QuickRangeStrip = ({
  onRelativePreset,
  onYearPreset,
  activeKey,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isAtEnd, setIsAtEnd] = useState(false);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const { scrollLeft, scrollWidth, clientWidth } = target;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
    if (atEnd !== isAtEnd) {
      setIsAtEnd(atEnd);
    }
  };

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.border.secondary}`,
        borderRadius: `${theme.shape.borderRadius * 2.5}px`,
        position: "relative",
        height: 48,
        overflow: "hidden",
      }}
    >
      <Box
        onScroll={handleScroll}
        sx={{
          height: "100%",
          overflowY: "hidden",
          overflowX: "auto",
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.border.secondary,
          },
        }}
      >
        <Box
          display="flex"
          gap={4}
          mb={1.5}
          sx={{
            pl: 3.75,
            alignItems: "center",
            height: "100%",
            minWidth: "396px",
          }}
        >
          <QuickRange
            active={activeKey === "1M"}
            onClick={() => onRelativePreset(1, "1M")}
          >
            {t("intervalSelector.compact.oneMonth")}
          </QuickRange>
          <QuickRange
            active={activeKey === "6M"}
            onClick={() => onRelativePreset(6, "6M")}
          >
            {t("intervalSelector.compact.sixMonths")}
          </QuickRange>
          <QuickRange
            active={activeKey === "1Y"}
            onClick={() => onRelativePreset(12, "1Y")}
          >
            {t("intervalSelector.compact.oneYear")}
          </QuickRange>
          {Array.from({ length: 4 }).map((_, index) => {
            const year = new Date().getFullYear() - index;
            const key = `year-${year}`;
            return (
              <QuickRange
                key={year}
                onClick={() => onYearPreset(year)}
                active={activeKey === key}
              >
                {year}
              </QuickRange>
            );
          })}
        </Box>
      </Box>
      {!isAtEnd && (
        <Box
          sx={{
            width: 40,
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 4,
            pointerEvents: "none",
            background:
              "linear-gradient(to right, transparent, transparent 10%, " +
              theme.palette.background.paper +
              " 100%)",
          }}
        />
      )}
    </Box>
  );
};
